import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n, translateTimeline } from "../lib/i18n";
import { useLivePoll } from "../lib/live";
import {
  StatusChip,
  STATUS_KEYS,
  fmtDate,
  docProtocol,
  sortDocumentsByProtocol,
  boardColumnForDocument,
  displayStatusForDocument,
  isRefusedDocument,
  documentMovementHistory,
  isRefusedOccurrence,
} from "../lib/format";
import { formatTaxIdInput, formatZipCodeInput, digitsOnly } from "../lib/tax-id";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

import { toast } from "sonner";
import { GripVertical, Lock, MapPin, User, FileText, History, Loader2 } from "lucide-react";

function formFromDoc(doc, tenants) {
  const tn = tenants.find((x) => x.id === doc.tenant_id) || tenants.find((x) => x.name === doc.notary_office);
  return {
    protocol_number: doc.protocol_number || doc.number || "",
    recipient_name: doc.recipient_name || "",
    tax_id: doc.recipient_cpf || doc.recipient_cnpj || "",
    address: doc.address || "",
    neighborhood: doc.neighborhood || "",
    city: doc.city || "",
    state: doc.state || "",
    zip_code: formatZipCodeInput(doc.zip_code || ""),
    lat: doc.lat ?? null,
    lng: doc.lng ?? null,
    tenant_id: doc.tenant_id || tn?.id || "",
    notary_office: doc.notary_office || tn?.name || "",
    deliverer_id: doc.deliverer_id || "",
    sector_id: doc.sector_id || "",
    status: displayStatusForDocument(doc) || "received",
  };
}

function DocumentDetailDialog({ docId, users, onClose, onReviewed, t }) {
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [form, setForm] = useState(null);
  const [lookingUpCep, setLookingUpCep] = useState(false);
  const lastLookedUpCep = useRef("");
  const cepAbort = useRef(null);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onReviewedRef = useRef(onReviewed);
  onReviewedRef.current = onReviewed;

  useEffect(() => {
    if (!docId) {
      setDetail(null);
      setForm(null);
      setSectors([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/documents/${docId}`),
      api.get("/tenants").catch(() => ({ data: [] })),
      api.get("/sectors").catch(() => ({ data: [] })),
    ])
      .then(async ([docRes, tenantRes, sectorRes]) => {
        if (cancelled) return;
        let doc = docRes.data;
        const list = tenantRes.data || [];
        setTenants(list);
        setSectors(sectorRes.data || []);
        if (!doc?.tenant_id && doc?.city) {
          try {
            const { data } = await api.patch(`/documents/${doc.id}`, { resolve_notary_by_city: true });
            if (!cancelled && data?.tenant_id) {
              doc = data;
              onReviewedRef.current?.();
            }
          } catch {
            // The document stays editable when the city does not match a single notary.
          }
        }
        if (cancelled) return;
        setDetail(doc);
        setForm(formFromDoc(doc, list));
        lastLookedUpCep.current = "";
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Falha ao carregar documento");
        onCloseRef.current?.();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [docId]);

  if (!docId) return null;

  const phaseLocked = detail ? canceledColumnLocked(detail, user) : false;
  const sectorOptions = sectors.filter((sector) => sector.tenant_id === form?.tenant_id);

  const lookupCep = async (cep) => {
    const digits = digitsOnly(cep);
    if (digits.length !== 8 || digits === lastLookedUpCep.current) return;
    lastLookedUpCep.current = digits;
    cepAbort.current?.abort();
    const controller = new AbortController();
    cepAbort.current = controller;
    setLookingUpCep(true);
    try {
      const { data } = await api.get(`/cep/${digits}`, { signal: controller.signal });
      setForm((f) => ({
        ...f,
        zip_code: formatZipCodeInput(data.zip_code || digits),
        address: data.address || f.address,
        neighborhood: data.neighborhood || f.neighborhood,
        city: data.city || f.city,
        state: data.state || f.state,
        lat: data.lat ?? null,
        lng: data.lng ?? null,
      }));
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
      lastLookedUpCep.current = "";
      toast.error(formatApiError(err?.response?.data?.detail) || t("cep_lookup_fail"));
    } finally {
      if (cepAbort.current === controller) setLookingUpCep(false);
    }
  };

  const setField = (key, raw) => {
    let value = raw;
    if (key === "tax_id") value = formatTaxIdInput(raw);
    if (key === "zip_code") value = formatZipCodeInput(raw);
    if (key === "state") value = raw.toUpperCase().slice(0, 2);
    if (key === "zip_code" && digitsOnly(value).length !== 8) {
      setForm((f) => ({ ...f, zip_code: value, lat: null, lng: null }));
      return;
    }
    setForm((f) => ({ ...f, [key]: value }));
    if (key === "zip_code" && digitsOnly(value).length === 8) void lookupCep(value);
  };

  const onTenantChange = (tenantId) => {
    const tn = tenants.find((x) => x.id === tenantId);
    setForm((f) => {
      const sectorStillValid = sectors.some((sector) => sector.id === f.sector_id && sector.tenant_id === tenantId);
      return {
        ...f,
        tenant_id: tenantId,
        notary_office: tn?.name || "",
        sector_id: sectorStillValid ? f.sector_id : "",
      };
    });
  };

  const onDelivererChange = async (value) => {
    if (!detail?.id) return;
    const delivererId = value === "__none__" ? null : value;
    setForm((f) => ({ ...f, deliverer_id: delivererId || "" }));
    setBusy(true);
    try {
      const payload = { deliverer_id: delivererId };
      const early = ["received", "sorting", "ready_for_route", "assigned"];
      if (delivererId && early.includes(detail.status)) payload.status = "out_for_delivery";
      const { data } = await api.patch(`/documents/${detail.id}`, payload);
      setDetail(data);
      setForm((f) => ({
        ...f,
        deliverer_id: data.deliverer_id || "",
        status: displayStatusForDocument(data) || f.status,
      }));
      toast.success(t("deliverer_assigned_ok"));
      onReviewed?.();
    } catch (err) {
      setForm((f) => ({ ...f, deliverer_id: detail.deliverer_id || "" }));
      toast.error(formatApiError(err?.response?.data?.detail) || t("deliverer_assign_fail"));
    } finally {
      setBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form?.tenant_id) {
      toast.error(t("dlg_select_notary"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        protocol_number: form.protocol_number,
        recipient_name: form.recipient_name,
        tax_id: form.tax_id,
        address: form.address,
        neighborhood: form.neighborhood || undefined,
        city: form.city,
        state: form.state || undefined,
        zip_code: form.zip_code || undefined,
        lat: form.lat,
        lng: form.lng,
        notary_office: form.notary_office,
        tenant_id: form.tenant_id,
        sector_id: form.sector_id || null,
      };
      if (!phaseLocked && form.status && form.status !== displayStatusForDocument(detail)) {
        payload.status = form.status;
      }
      const { data } = await api.patch(`/documents/${detail.id}`, payload);
      setDetail(data);
      setForm(formFromDoc(data, tenants));
      toast.success(t("doc_updated"));
      onReviewed?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t("doc_update_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!docId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="rounded-3xl max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="operational-doc-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {loading ? "…" : docProtocol(detail)}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-sm text-[var(--text-2)]">Carregando…</div>
        )}

        {detail && form && !loading && (
          <form onSubmit={save} className="space-y-4">
            <div className="flex items-center gap-2">
              <StatusChip status={displayStatusForDocument(detail)} t={t} />
              {isRefusedDocument(detail) && (
                <span className="text-xs font-medium text-[var(--text-2)]">Recusado</span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <FieldRow label={t("doc_phase")}>
                <Select
                  value={form.status || "received"}
                  onValueChange={(value) => setField("status", value)}
                  disabled={phaseLocked || busy}
                >
                  <SelectTrigger className="rounded-xl h-9 mt-0.5" data-testid="operational-input-phase">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_KEYS.map((status) => (
                      <SelectItem key={status} value={status}>{t(`status_${status}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>
              <FieldRow label={t("doc_sector")}>
                <Select
                  value={form.sector_id || "__none__"}
                  onValueChange={(value) => setField("sector_id", value === "__none__" ? "" : value)}
                  disabled={busy || !form.tenant_id}
                >
                  <SelectTrigger className="rounded-xl h-9 mt-0.5" data-testid="operational-input-sector">
                    <SelectValue placeholder={t("doc_sector_none")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("doc_sector_none")}</SelectItem>
                    {sectorOptions.map((sector) => (
                      <SelectItem key={sector.id} value={sector.id}>{sector.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>
              <FieldRow icon={User} label={t("recipient")}>
                <Input
                  value={form.recipient_name}
                  onChange={(e) => setField("recipient_name", e.target.value)}
                  className="rounded-xl h-9 mt-0.5"
                  data-testid="operational-input-recipient_name"
                />
              </FieldRow>
              <FieldRow icon={MapPin} label={t("city")}>
                <Input
                  value={form.city}
                  onChange={(e) => setField("city", e.target.value)}
                  className="rounded-xl h-9 mt-0.5"
                  data-testid="operational-input-city"
                />
              </FieldRow>
              <FieldRow icon={FileText} label={t("notary")} span>
                <Select value={form.tenant_id || undefined} onValueChange={onTenantChange}>
                  <SelectTrigger className="rounded-xl h-9 mt-0.5" data-testid="operational-input-notary">
                    <SelectValue placeholder={t("dlg_select_notary")} />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map((tn) => (
                      <SelectItem key={tn.id} value={tn.id}>{tn.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>
              <FieldRow label={t("address")} span>
                <Input
                  value={form.address}
                  onChange={(e) => setField("address", e.target.value)}
                  className="rounded-xl h-9 mt-0.5"
                  data-testid="operational-input-address"
                />
              </FieldRow>
              <FieldRow label={t("document_number")}>
                <Input
                  value={form.protocol_number}
                  onChange={(e) => setField("protocol_number", e.target.value)}
                  className="rounded-xl h-9 mt-0.5"
                  data-testid="operational-input-protocol_number"
                />
              </FieldRow>
              <FieldRow label={t("deliverer")}>
                <Select
                  value={form.deliverer_id || "__none__"}
                  onValueChange={onDelivererChange}
                  disabled={busy}
                >
                  <SelectTrigger className="rounded-xl h-9 mt-0.5" data-testid="operational-input-deliverer">
                    <SelectValue placeholder={t("no_deliverer")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("so_deliverer_none")}</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>
              <FieldRow label={t("dlg_tax_id")}>
                <Input
                  value={form.tax_id}
                  onChange={(e) => setField("tax_id", e.target.value)}
                  className="rounded-xl h-9 mt-0.5"
                  data-testid="operational-input-tax_id"
                />
              </FieldRow>
              <FieldRow label={t("dlg_zip")}>
                <div className="relative">
                  <Input
                    value={form.zip_code}
                    onChange={(e) => setField("zip_code", e.target.value)}
                    className="rounded-xl h-9 mt-0.5"
                    inputMode="numeric"
                    data-testid="operational-input-zip_code"
                  />
                  {lookingUpCep && (
                    <Loader2 size={12} className="animate-spin text-[var(--brand)] absolute right-2 top-3" />
                  )}
                </div>
              </FieldRow>
              <FieldRow label={t("dlg_neighborhood")}>
                <Input
                  value={form.neighborhood}
                  onChange={(e) => setField("neighborhood", e.target.value)}
                  className="rounded-xl h-9 mt-0.5"
                  data-testid="operational-input-neighborhood"
                />
              </FieldRow>
              <FieldRow label={t("dlg_state")}>
                <Input
                  value={form.state}
                  onChange={(e) => setField("state", e.target.value)}
                  className="rounded-xl h-9 mt-0.5"
                  maxLength={2}
                  data-testid="operational-input-state"
                />
              </FieldRow>
              <FieldRow label={t("created")} value={fmtDate(detail.created_at)} />
              <FieldRow label={t("updated")} value={fmtDate(detail.updated_at)} />
            </div>

            {detail.notes && (
              <div className="text-sm rounded-2xl bg-[var(--surface-2)] p-3">
                <div className="text-xs font-semibold text-[var(--text-2)] mb-1">{t("notes")}</div>
                {detail.notes}
              </div>
            )}

            <Button
              type="submit"
              disabled={saving || busy || !form.protocol_number || !form.recipient_name || !form.address || !form.city || !form.tenant_id}
              className="w-full rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-11"
              data-testid="operational-save-doc"
            >
              {saving ? "..." : t("dlg_save")}
            </Button>

            {documentMovementHistory(detail).length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-[var(--text-2)] mb-2">
                  <History size={13} /> {t("history")}
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {documentMovementHistory(detail).map((h, i) => (
                    <div key={`${h.status}-${h.at}-${i}`} className="flex items-start justify-between gap-2 text-xs rounded-xl bg-[var(--surface-2)] px-3 py-2">
                      <div>
                        <StatusChip status={isRefusedOccurrence(h.note) ? "delivered" : h.status} t={t} />
                        {h.note && <div className="text-[var(--text-2)] mt-1">{translateTimeline(t, "note", h.note)}</div>}
                      </div>
                      <span className="font-mono text-[var(--text-3)] shrink-0">{fmtDate(h.at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ icon: Icon, label, value, span, children }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-2)]">
        {Icon && <Icon size={11} />}
        {label}
      </div>
      {children || <div className="text-sm font-medium text-[var(--text-1)] mt-0.5">{value || "—"}</div>}
    </div>
  );
}

function canceledColumnLocked(doc, user) {
  return boardColumnForDocument(doc) === "canceled" && user?.role !== "admin";
}

export default function Operational() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/documents");
    setDocs(sortDocumentsByProtocol(data));
  }, []);

  useEffect(() => {
    api.get("/users?role=deliverer").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);
  useLivePoll(load, 15000, !selectedId);

  const onDrop = async (columnStatus) => {
    if (!dragging) return;
    const doc = dragging;
    setDragging(null);
    const targetStatus = columnStatus;
    if (boardColumnForDocument(doc) === targetStatus) return;
    if (canceledColumnLocked(doc, user)) {
      toast.error(t("canceled_move_blocked"));
      return;
    }
    setDocs((prev) => prev.map((d) => d.id === doc.id ? { ...d, status: targetStatus } : d));
    try {
      await api.patch(`/documents/${doc.id}`, { status: targetStatus });
      toast.success(`Status: ${t(`status_${targetStatus}`)}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Falha ao atualizar status");
      load();
    }
  };

  const grouped = STATUS_KEYS.reduce((acc, s) => ({
    ...acc,
    [s]: docs.filter((d) => boardColumnForDocument(d) === s),
  }), {});

  return (
    <div className="space-y-4">
      <div className="card-soft px-5 py-4">
        <div className="text-xs text-[var(--text-2)] font-medium">Operações</div>
        <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">{t("nav_operational")}</h1>
        <p className="text-xs text-[var(--text-2)] mt-1">{t("drag_drop_hint")}</p>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
        {STATUS_KEYS.map((s) => (
          <div
            key={s}
            className="kanban-column card-soft flex flex-col max-h-[75vh] overflow-hidden"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(s)}
            data-testid={`kanban-column-${s}`}
          >
            <div className="p-3 border-b border-[var(--border-c)] bg-[var(--surface-2)] flex items-center justify-between sticky top-0">
              <StatusChip status={s} t={t} />
              <div className="font-mono text-xs text-[var(--text-2)] font-semibold">{grouped[s].length}</div>
            </div>
            <div className="p-2 space-y-2 overflow-y-auto flex-1">
              {grouped[s].map((d) => (
                <div
                  key={d.id}
                  className="draggable-card rounded-2xl border border-[var(--border-c)] bg-[var(--surface)] p-3 hover:border-[var(--brand)]/40 hover:shadow-sm transition"
                  data-testid={`kanban-card-${d.number}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedId(d.id)}
                      className="flex-1 text-left min-w-0"
                      data-testid={`kanban-card-open-${d.number}`}
                    >
                    <div className="font-mono text-[10px] text-[var(--text-2)]">{d.number}</div>
                      <div className="font-semibold text-sm mt-1 text-[var(--text-1)]">{d.protocol_number}</div>
                      <div className="text-xs text-[var(--text-2)] mt-0.5">{d.city} · {d.notary_office || "—"}</div>
                      {isRefusedDocument(d) && (
                        <div className="text-[10px] font-medium text-[var(--text-2)] mt-1">Recusado</div>
                      )}
                    </button>
                    {canceledColumnLocked(d, user) ? (
                      <div
                        className="p-1 -mr-1 text-[var(--text-3)]"
                        title={t("canceled_move_blocked")}
                        data-testid={`kanban-card-locked-${d.number}`}
                      >
                        <Lock size={14} />
                      </div>
                    ) : (
                    <div
                      draggable
                      onDragStart={() => setDragging(d)}
                      className="cursor-grab active:cursor-grabbing p-1 -mr-1 text-[var(--text-3)] hover:text-[var(--brand)]"
                      title={t("drag_drop_hint")}
                      data-testid={`kanban-card-drag-handle-${d.number}`}
                    >
                      <GripVertical size={14} />
                    </div>
                    )}
                  </div>
                </div>
              ))}
              {grouped[s].length === 0 && <div className="text-xs text-[var(--text-3)] text-center py-6">—</div>}
            </div>
          </div>
        ))}
      </div>

      <DocumentDetailDialog
        docId={selectedId}
        users={users}
        onClose={() => setSelectedId(null)}
        onReviewed={load}
        t={t}
      />
    </div>
  );
}
